import React, { useContext, useState, useEffect } from 'react';
import { Button, Box, Typography, TextField, MenuItem } from '@mui/material';
import { useNextProjectStep } from '../../../hooks/useNextProjectStep';
import { usePreviousProjectStep } from '../../../hooks/usePreviousProjectStep';
import { ProjectContext } from '../../../layouts/CreateProjectLayout';
import axios from 'axios';
import MaskedImage from '../../MaskedImage';
import { useNavigate } from 'react-router-dom';


const TestingStep = () => {
  const navigate = useNavigate();
  const { project, canEditProject } = useContext(ProjectContext);
  const { goToNextStep } = useNextProjectStep();
  const { goToPreviousStep } = usePreviousProjectStep();

  const [models, setModels] = useState([]);
  const [selectedModelId, setSelectedModelId] = useState('');
  const [selectedTaskOption, setSelectedTaskOption] = useState('Test');
  const [taskId, setTaskId] = useState('');

  const [testingSession, setTestingSession] = useState(null);
  const [inferences, setInferences] = useState([]);
  const [currentInferenceIndex, setCurrentInferenceIndex] = useState(0);

  const [isChecking, setIsChecking] = useState(false);
  const [isRunning, setIsRunning] = useState(false);

    const handleNext = () => {
    navigate('/');
  };


  // Fetch all models for the project
  useEffect(() => {
    const fetchModels = async () => {
      if (!project?.id) return;
      try {
        const response = await axios.get(`/models?project_id=${project.id}`);
        setModels(response.data);
      } catch (error) {
        console.error('Error fetching models:', error);
      }
    };
    fetchModels();
  }, [project?.id]);

  // Determine overlay type for display
  const selectedModel = models.find((m) => String(m.id) === String(selectedModelId));
  const overlayType = selectedModel?.info?.modelTask?.toLowerCase().includes('bbox') ? 'bbox' : 'mask';

  const currentInference = inferences[currentInferenceIndex];

  const currentGt = Array.isArray(
    currentInference?.status_msg?.gt
  )
    ? currentInference.status_msg.gt
    : [];
  const hasBboxGt = currentGt.some((entry) => entry?.type === 'bbox');
  const hasMaskGt = currentGt.some((entry) => entry?.rle_gt && entry?.type !== 'bbox');
  const displayOverlayType = overlayType === 'bbox' && !hasBboxGt && hasMaskGt ? 'mask'
    : overlayType === 'mask' && hasBboxGt && !hasMaskGt ? 'bbox'
    : overlayType;

  // Reset inference index when inferences change
  useEffect(() => setCurrentInferenceIndex(0), [inferences]);

  // Navigate between inference images
  const handleNextInference = () => { if (inferences.length) setCurrentInferenceIndex((prev) => (prev + 1) % inferences.length); };
  const handlePrevInference = () => { if (inferences.length) setCurrentInferenceIndex((prev) => (prev - 1 + inferences.length) % inferences.length); };

  // Check if a testing session already exists for this model + task
  useEffect(() => {
    const checkExistingTesting = async () => {
      if (!selectedModelId) return;

      setIsChecking(true);
      try {
        const params = { model_id: selectedModelId, cvat_task_filter: selectedTaskOption };
        if (selectedTaskOption === 'TaskID' && taskId) params.cvat_task_id = parseInt(taskId);

        const response = await axios.get('/testings', { params });
        const sessions = response.data;

        const matchingSession = sessions?.find((s) =>
          selectedTaskOption === 'TaskID'
            ? s.cvat_task_filter === 'TaskID' && s.cvat_task_id === parseInt(taskId)
            : s.cvat_task_filter === selectedTaskOption
        );

        if (matchingSession) {
          setTestingSession(matchingSession);
          setInferences(matchingSession.inferences || []);
        } else {
          setTestingSession(null);
          setInferences([]);
        }
      } catch (error) {
        console.error('Error checking testing session:', error);
        setTestingSession(null);
        setInferences([]);
      } finally {
        setIsChecking(false);
      }
    };

    checkExistingTesting();
  }, [selectedModelId, selectedTaskOption, taskId]);

  // Start a new testing session
  const startTesting = async (cvatId, modelId, selectedTaskOption, taskIdValue) => {
    setIsRunning(true);
    try {
      const taskIdNumber = selectedTaskOption === 'TaskID' && taskIdValue ? parseInt(taskIdValue) : undefined;
      const response = await axios.post('/testings', {
        cvat_id: cvatId,
        model_id: modelId,
        cvat_task_filter: selectedTaskOption,
        cvat_task_id: taskIdNumber,
      });
      const session = response.data;
      setTestingSession(session);
      setInferences(session.inferences || []);
    } catch (error) {
      console.error('Error starting testing session:', error);
    } finally {
      setIsRunning(false);
    }
  };

  // Poll queued testing session
  useEffect(() => {
    if (!testingSession?.id) return;
    if (testingSession.status === 'completed') return;

    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`/testings/${testingSession.id}`);
        const updated = response.data;
        setTestingSession(updated);
        setInferences(updated.inferences || []);
        if (updated.status === 'completed') clearInterval(interval);
      } catch (error) {
        console.error('Polling error:', error);
        clearInterval(interval);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [testingSession]);

  return (
    <Box sx={{ mx: 'auto', mt: 1, borderRadius: 4, overflow: 'hidden', width: { xs: '80%', sm: '70%' }, display: 'flex', flexDirection: 'column', p: 1 }}>
      <Typography variant="h3" fontWeight={700} color="black" mt={4}>Testing</Typography>
      <Typography variant="h6" color="rgb(158, 151, 151)" mt={2}>Test and evaluate your trained models.</Typography>

      {/* Model + Task Selection */}
      <Box sx={{ width: '95%', mt: 1, display: 'flex', flexDirection: 'column', gap: 2, p: 1 }}>
        <Box sx={{ mt: 2, width: '80%', display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <TextField
            fullWidth
            select
            label="Select the model to evaluate"
            value={selectedModelId}
            onChange={(e) => setSelectedModelId(e.target.value)}
            sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 38 * 5 + 8 } } } }}
          >
            {models.length === 0 ? <MenuItem disabled>No models available</MenuItem> :
              models.map((model) => <MenuItem key={model.id} value={model.id}>{model.name}</MenuItem>)}
          </TextField>

          <Box sx={{ width: '30%', display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              select
              label="Select CVAT Task"
              value={selectedTaskOption}
              onChange={(e) => setSelectedTaskOption(e.target.value)}
              disabled={!canEditProject}
              sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            >
              <MenuItem value="Train">Train</MenuItem>
              <MenuItem value="Validation">Val</MenuItem>
              <MenuItem value="Test">Test</MenuItem>
              <MenuItem value="TaskID">Task ID</MenuItem>
            </TextField>
            {selectedTaskOption === 'TaskID' && (
              <TextField type="number" label="Task ID" value={taskId} onChange={(e) => setTaskId(e.target.value)}
                disabled={!canEditProject}
                sx={{ width: 100, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
            )}
          </Box>

          <Button variant="contained" disabled={!canEditProject} sx={{ height: '56px', borderRadius: '12px', backgroundColor: '#16a4b0', textTransform: 'none', whiteSpace: 'nowrap', '&:hover': { backgroundColor: '#00707c' } }}
            onClick={() => startTesting(parseInt(project.cvat_id), parseInt(selectedModelId), selectedTaskOption, taskId)}>
            Test
          </Button>
        </Box>
      </Box>

      {/* Inference Images */}
      <Box sx={{ mt: 2, width: '100%', display: 'flex', justifyContent: 'center' }}>
        {inferences.length === 0 ? (
          <Typography>No inference images yet.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', gap: 1 }}>
              <Button onClick={handlePrevInference} disabled={isRunning || isChecking} sx={{ fontSize: '3rem', color: '#16a4b0' }}>‹</Button>
            <Box sx={{ width: '100%', maxWidth: 1500, display: 'flex', gap: 4, justifyContent: 'center', alignItems: 'flex-start' }}>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography variant="subtitle1" fontWeight={400} sx={{ mb: 1, textAlign: 'center' }}>Ground Truth</Typography>
                <MaskedImage
                  imageUrl={inferences[currentInferenceIndex].input_image}
                  rleMasks={inferences[currentInferenceIndex].status_msg?.gt}
                  type={displayOverlayType}
                />
              </Box>

              {/* Result Image */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography variant="subtitle1" fontWeight={400} sx={{ mb: 1, textAlign: 'center' }}>Prediction</Typography>
                <MaskedImage
                  imageUrl={inferences[currentInferenceIndex].result_image}
                />
              </Box>
            </Box>
              <Button onClick={handleNextInference} disabled={isRunning || isChecking} sx={{ fontSize: '3rem', color: '#16a4b0' }}>›</Button>
            </Box>
            <Typography variant="caption">{currentInferenceIndex + 1} / {inferences.length}</Typography>
          </Box>
        )}
      </Box>

      {/* Navigation Buttons */}
      <Box sx={{ padding: 2, width: '100%', display: 'flex', justifyContent: 'center', gap: 4, mt: 4 }}>
        <Button variant="outlined" onClick={goToPreviousStep} sx={{ borderRadius: '12px', textTransform: 'none', fontSize: '16px', width: '120px', color: '#16a4b0', borderColor: '#16a4b0', '&:hover': { backgroundColor: '#f0f0f0', borderColor: '#16a4b0' } }}>Previous</Button>
        {/* <Button variant="contained" onClick={goToNextStep} sx={{ borderRadius: '12px', backgroundColor: '#16a4b0', color: 'white', textTransform: 'none', fontSize: '16px', width: '120px', '&:hover': { backgroundColor: '#00707c' } }}>Next</Button> */}
                    <Button variant="contained" onClick={handleNext} sx={{ borderRadius: '12px', backgroundColor: '#16a4b0', color: 'white', textTransform: 'none', fontSize: '16px', width: '120px', '&:hover': { backgroundColor: '#00707c' } }}>
                      Done
                    </Button>
      </Box>
    </Box>
  );
};

export default TestingStep;